import { Button, type ButtonProps } from "$components/button/button";
import { SearchIcon } from "$components/icons";
import {
  FormControl,
  FormHelperText,
  type InputBaseComponentProps,
  makeStyles,
  OutlinedInput,
} from "@material-ui/core";
import React from "react";

const BUTTON_WIDTH = 44;

const useStyles = makeStyles((theme) => ({
  root: {
    // Just needs to be > half of the height
    borderRadius: 100,
    paddingLeft: 26,
  },
  input: {
    paddingTop: theme.spacing(),
    paddingRight: BUTTON_WIDTH - theme.spacing(),
  },
}));

const searchIconButtonStyles = makeStyles({
  root: {
    minWidth: "auto",
    position: "absolute",
    top: 4,
    right: 4,
    width: BUTTON_WIDTH,
    fontSize: 17,
    padding: 0,
  },
});

const searchLabelButtonStyles = makeStyles({
  root: {
    minWidth: "auto",
    position: "absolute",
    top: 4,
    right: 4,
    marginTop: 2,
    marginRight: 2,
  },
});

const SearchLabelledButton = ({ label, ...props }) => {
  const classes = searchLabelButtonStyles();

  return (
    <Button className={classes.root} aria-label="Submit" {...props}>
      {label}
    </Button>
  );
};

const SearchIconButton = (props) => {
  const classes = searchIconButtonStyles();

  return (
    <Button className={classes.root} aria-label="Submit" {...props}>
      <SearchIcon />
    </Button>
  );
};

interface SearchFieldButtonProps extends Omit<ButtonProps, "variant"> {
  variant?: "text" | "outlined" | "contained" | "labelled";
  label?: React.ReactNode;
}

export type SearchFieldProps = InputBaseComponentProps & {
  loading?: boolean;
  onSubmit?: Function;
  buttonProps?: SearchFieldButtonProps;
  helperText?: React.ReactNode;
  placeholder?: string;
  inputRef?: React.Ref<HTMLInputElement>;
};

const SearchField = ({
  onSubmit: handleSubmit = undefined,
  loading: isLoading = false,
  buttonProps = {},
  helperText = undefined,
  placeholder = "Search...",
  inputRef,
  ...searchFieldProps
}: SearchFieldProps) => {
  const classes = useStyles();
  const {
    variant: buttonVariant,
    label: buttonLabel,
    ...filteredButtonProps
  } = buttonProps;

  return (
    <FormControl variant="outlined" fullWidth>
      <OutlinedInput
        classes={classes}
        type="text"
        placeholder={placeholder}
        inputProps={searchFieldProps}
        inputRef={inputRef}
      />
      {buttonVariant === "labelled" ? (
        <SearchLabelledButton
          onClick={handleSubmit}
          loading={isLoading}
          label={buttonLabel}
          {...filteredButtonProps}
        />
      ) : (
        <SearchIconButton
          onClick={handleSubmit}
          loading={isLoading}
          {...buttonProps}
        />
      )}
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};

export default SearchField;
